import { useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router";
import Sidebar from "./components/sidebar";
import { useAdminMode } from "./contexts/AdminModeContext";
import "./App.css";
import { ColorProvider } from "./contexts/ColorContext";
import { appConfig, saveRuntimeOverrides } from "@/appConfig";
import { useAppAuth } from "./hooks/useAppAuth";
import api from "./http";

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role, isLoading, getAccessToken } = useAppAuth();
  const { adminMode } = useAdminMode();
  const [ssoHandoff, setSsoHandoff] = useState(false);
  const [isHydratingAppSettings, setIsHydratingAppSettings] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ssoToken = params.get("token");
    if (ssoToken && !ssoHandoff) {
      setSsoHandoff(true);
      fetch(`${import.meta.env.VITE_BACKEND_URL}/auth/sso-handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: ssoToken }),
      })
        .then((res) => {
          if (!res.ok) throw new Error("SSO handoff failed");
          return res.json();
        })
        .then((data) => {
          localStorage.setItem("sso_session", JSON.stringify(data));
          localStorage.setItem("login_source", "spedlogswiss");
          window.history.replaceState({}, "", location.pathname);
          window.location.reload();
        })
        .catch((err) => {
          console.error("SSO handoff error:", err);
          setSsoHandoff(false);
        });
    }
  }, [location.search, location.pathname, ssoHandoff]);

  useEffect(() => {
    const hydrateAppSettings = async () => {
      if (!user) {
        setIsHydratingAppSettings(false);
        return;
      }

      try {
        const token = await getAccessToken();
        if (!token) {
          setIsHydratingAppSettings(false);
          return;
        }

        const settings = await api.getAppSettings(token);
        const nextAssistantName = settings.assistant_name;
        const nextWelcomeMessage = settings.welcome_message;
        const nextWelcomeMessages = settings.welcome_messages || {};
        const nextAssistantPhoto = settings.assistant_photo || "";

        const hasChanged =
          nextAssistantName !== appConfig.assistantName ||
          nextWelcomeMessage !== appConfig.welcomeMessage ||
          JSON.stringify(nextWelcomeMessages) !==
            JSON.stringify(appConfig.welcomeMessages || {}) ||
          nextAssistantPhoto !== (appConfig.assistantPhoto || "");

        if (hasChanged) {
          saveRuntimeOverrides({
            assistantName: nextAssistantName,
            welcomeMessageTemplate: nextWelcomeMessage,
            welcomeMessages: nextWelcomeMessages,
            assistantPhoto: nextAssistantPhoto,
          });
        }
      } catch (error) {
        console.error("Failed to load app settings:", error);
      }

      setIsHydratingAppSettings(false);
    };

    hydrateAppSettings();
  }, [user, getAccessToken]);

  useEffect(() => {
    if (!isLoading && user) {
      const userRole = role;
      const currentPath = location.pathname;

      if (currentPath === "/") {
        return;
      }

      if (userRole === "member" && !currentPath.startsWith("/assistant")) {
        navigate("/assistant", { replace: true });
      }

      if (userRole === "admin" || userRole === "superadmin") {
        if (adminMode === "member" && !currentPath.startsWith("/assistant")) {
          navigate("/assistant", { replace: true });
        }
        else if (adminMode === "admin") {
          const allowedAdminRoutes = [
            "/documents",
            "/users",
            "/answers",
            "/settings",
            "/analytics",
          ];
          const pathSegments = currentPath.split("/").filter(Boolean);
          const isAllowedAdminRoute = allowedAdminRoutes.some((route) => {
            const normalizedRoute = route.replace(/^\//, "");
            if (
              currentPath === route ||
              currentPath.startsWith(`${route}/`)
            ) {
              return true;
            }
            return pathSegments.includes(normalizedRoute);
          });

          if (!isAllowedAdminRoute) {
            navigate("/documents", { replace: true });
          }
        }
      }
    }
  }, [user, role, isLoading, navigate, adminMode, location.pathname]);

  if (isLoading || ssoHandoff || isHydratingAppSettings) {
    const loadingColor = appConfig.features.isDemoMode
      ? (localStorage.getItem("themeColor") || "#2563EB")
      : appConfig.themeColor;
    
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <div className="flex space-x-2 transform translate-y-[0.75px]">
          <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.3s]" style={{ backgroundColor: loadingColor }} />
          <div className="w-2 h-2 rounded-full animate-bounce [animation-delay:-0.15s]" style={{ backgroundColor: loadingColor }} />
          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: loadingColor }} />
        </div>
      </div>
    );
  }

  if (!user) {
    const params = new URLSearchParams(window.location.search);
    if (!params.get("token")) {
      window.location.href = "https://portal.spedlogswiss.com/";
      return null;
    }
  }

  return (
    <ColorProvider>
      <div
        className="flex h-screen w-screen"
        style={{
          background: `linear-gradient(0deg, #FFFFFF, #FFFFFF), linear-gradient(180deg, rgba(238, 242, 255, 0) 81.26%, #EEF2FF 99.1%)`,
        }}
      >
        <Sidebar />
        <main className="flex-1 overflow-hidden pt-16 lg:pt-0">
          <Outlet />
        </main>
      </div>
    </ColorProvider>
  );
};

export default App;
